import { promises as fs } from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";

import type { Logger } from "pino";

const SEED_DIR_NAME = ".appostle";
const BRAND_DIR_NAME = "brand";
const TEMPLATES_DIR_NAME = "schema-templates";

/** Prose .md filenames seeded into `.appostle/brand/prose/`. */
const PROSE_FILES = ["animations.md", "layout.md", "photography.md", "voice.md"];

/** Asset subdirectories created empty so uploads have a target. */
const ASSET_DIRS = ["logo", "shape", "typefaces"];

/**
 * Resolves the on-disk location of the bundled brand schema templates.
 *
 * Co-located with this file in both source (`src/server/brand/schema-templates`)
 * and compiled output (`dist/server/server/brand/schema-templates`) — the build
 * pipeline copies the directory alongside the emitted `.js`, so a sibling
 * lookup works in dev (tsx) and prod (node dist) without branching.
 */
function resolveTemplatesDir(): string {
  const here = path.dirname(fileURLToPath(import.meta.url));
  return path.join(here, TEMPLATES_DIR_NAME);
}

/**
 * Idempotently seed `<rootPath>/.appostle/brand/` with the bundled brand
 * schema templates. If the target directory already exists we never touch it —
 * humans (and later, scrapers) own anything inside, and we must never overwrite
 * their work. This is the load-bearing invariant: the seeder is creation-only.
 *
 * New projects receive:
 *   - `.appostle/brand/tokens.json`       — empty-valued W3C DTCG template
 *   - `.appostle/brand/prose/*.md`         — 4 prose surface templates
 *   - `.appostle/brand/assets/{logo,shape,typefaces,role}/` — empty asset dirs
 */
export async function seedProjectBrand(
  rootPath: string,
  options?: { logger?: Logger },
): Promise<{ seeded: boolean; targetDir: string }> {
  const target = path.join(rootPath, SEED_DIR_NAME, BRAND_DIR_NAME);

  try {
    await fs.access(target);
    return { seeded: false, targetDir: target };
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    if (code !== "ENOENT") {
      throw error;
    }
  }

  const templatesDir = resolveTemplatesDir();

  // Create base brand directory.
  await fs.mkdir(target, { recursive: true });

  // 1. Copy tokens.json template.
  const tokensTemplatePath = path.join(templatesDir, "tokens.json");
  try {
    await fs.access(tokensTemplatePath);
    await fs.copyFile(tokensTemplatePath, path.join(target, "tokens.json"));
  } catch (err) {
    options?.logger?.error(
      { err, tokensTemplatePath },
      "brand seed: tokens.json template unreadable; skipping",
    );
  }

  // 2. Copy prose .md templates.
  const proseTarget = path.join(target, "prose");
  await fs.mkdir(proseTarget, { recursive: true });
  const proseTemplatesDir = path.join(templatesDir, "prose");
  for (const file of PROSE_FILES) {
    const src = path.join(proseTemplatesDir, file);
    try {
      await fs.access(src);
      await fs.copyFile(src, path.join(proseTarget, file));
    } catch (err) {
      options?.logger?.warn(
        { err, src },
        `brand seed: prose template ${file} unreadable; skipping`,
      );
    }
  }

  // 3. Create empty asset directories so uploads have a target on first open.
  for (const dir of ASSET_DIRS) {
    await fs.mkdir(path.join(target, "assets", dir), { recursive: true });
  }

  options?.logger?.info({ targetDir: target }, "brand schema seeded");
  return { seeded: true, targetDir: target };
}
